/**
 * Firebase Web SDK — lazy singleton. Nothing initialises until a sign-in
 * button is actually pressed, so the app boots normally even when the
 * REACT_APP_FIREBASE_* env vars are missing (which they will be until the
 * admin uploads the config).
 *
 * Migration strategy (v42):
 *   * Google + Apple sign-in via Firebase run alongside legacy Emergent
 *     Google Auth for a 30-day dual-run window.
 *   * `isFirebaseConfigured()` gates the UI so we hide the buttons when
 *     the deployment hasn't set env vars yet.
 *   * When running inside Capacitor (iOS/Android app), we defer to the
 *     `@capacitor-firebase/authentication` native plugin so the flow uses
 *     the platform's native Google / Apple sign-in sheets instead of a
 *     web popup (required for App Store approval).
 */
import { initializeApp, getApps, getApp } from "firebase/app";
import {
  getAuth, GoogleAuthProvider, OAuthProvider,
  signInWithPopup, signOut as fbSignOut,
  signInWithEmailAndPassword, createUserWithEmailAndPassword,
  sendPasswordResetEmail, sendEmailVerification, updateProfile,
  browserLocalPersistence, setPersistence,
} from "firebase/auth";
// STATIC import (v88.1) — critical.
// The @capacitor-firebase/authentication export is a Proxy created by
// Capacitor's registerPlugin(). If we `return` that Proxy from an async
// function, JavaScript's Promise-resolution algorithm treats the Proxy as
// a thenable (because Proxy.then returns a function via the default get-trap),
// calls Proxy.then(resolve, reject), which sends the string "then" to the
// native bridge as if it were a real method name. The native side has no
// `then` method, so the callback never fires and `await` hangs forever —
// which was the "stuck on Signing in..." bug users saw on iOS device.
// Capacitor 6.2.1's Proxy handles $$typeof/toJSON/addListener/removeListener
// but NOT `then` — bug reproducible via any `return proxy` from async fn.
// Static-importing here means the Proxy exists at module load time and is
// never itself awaited; only its method-call return Promises are awaited.
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import axios from "axios";

const envConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

let _remoteConfig = null;      // filled after /auth/firebase/public-config succeeds
let _configPromise = null;     // dedupes concurrent fetches
let _statusEnabled = false;    // last-known backend status (are creds live?)

async function fetchRemoteConfig() {
  if (_remoteConfig) return _remoteConfig;
  if (_configPromise) return _configPromise;
  _configPromise = axios.get(
    `${process.env.REACT_APP_BACKEND_URL}/api/auth/firebase/public-config`,
    // Public endpoint — no cookie needed. `withCredentials: true` combined
    // with the backend's `Access-Control-Allow-Origin: *` is a CORS spec
    // violation that Chrome / iOS WKWebView reject, silently breaking the
    // config fetch on Capacitor. Keeping this a plain unauthenticated GET.
  ).then(({ data }) => {
    if (data?.configured && data?.apiKey && data?.projectId && data?.appId) {
      _remoteConfig = {
        apiKey: data.apiKey, authDomain: data.authDomain,
        projectId: data.projectId, storageBucket: data.storageBucket,
        messagingSenderId: data.messagingSenderId, appId: data.appId,
      };
    }
    return _remoteConfig;
  }).catch(() => null).finally(() => { _configPromise = null; });
  return _configPromise;
}

function resolveConfig() {
  if (envConfig.apiKey && envConfig.projectId && envConfig.appId) return envConfig;
  return _remoteConfig;
}

/**
 * Reports whether Firebase Auth is usable right now. Kicks off the remote
 * config fetch on first call so subsequent renders / clicks resolve fast.
 */
export async function ensureFirebaseReady() {
  // If .env has values, we trust them (frontend-only setups can bypass DB config).
  if (envConfig.apiKey && envConfig.projectId && envConfig.appId) {
    _statusEnabled = true;
    return true;
  }
  await fetchRemoteConfig();
  _statusEnabled = Boolean(_remoteConfig);
  return _statusEnabled;
}

/** Synchronous best-effort check used by the button-render gate. */
export function isFirebaseConfigured() {
  return Boolean(resolveConfig());
}

function isNativeCapacitor() {
  // Capacitor injects a global `window.Capacitor` object on iOS/Android
  // native builds; on web this is undefined so we take the popup path.
  return typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();
}

let _app = null;
function getFirebaseApp() {
  if (_app) return _app;
  const cfg = resolveConfig();
  if (!cfg) {
    throw new Error("Firebase is not configured — ask an admin to save Firebase credentials in Super Admin → Firebase.");
  }
  _app = getApps().length ? getApp() : initializeApp(cfg);
  return _app;
}

async function getFirebaseAuth() {
  const auth = getAuth(getFirebaseApp());
  try { await setPersistence(auth, browserLocalPersistence); }
  catch (_) { /* SSR / private mode — non-fatal */ }
  return auth;
}

/**
 * (Removed v88.1) `loadNativeAuthPlugin()` used to dynamic-import the plugin
 * and `return mod.FirebaseAuthentication`. That leaked a Capacitor Proxy
 * out of an async function, which triggered the missing-`.then`-trap bug
 * described in the top-of-file import comment (await hangs forever).
 * All call sites now use the statically-imported `FirebaseAuthentication`
 * directly.
 */

/** Kick off a Google sign-in flow and return the Firebase ID token. */
export async function signInWithGoogle() {
  if (isNativeCapacitor()) {
    // Native path (skipNativeAuth: false — matches our capacitor.config).
    // The plugin's NATIVE Firebase SDK signs the user in behind the sheet,
    // so we don't need to invoke the JS SDK's signInWithCredential — and
    // in fact SHOULDN'T, because that JS-SDK call hangs inside Capacitor
    // WKWebView (network layer to identitytoolkit.googleapis.com never
    // completes; the symptom is "Signing in…" spinner forever after the
    // native Google account sheet closes).
    //
    // Instead, ask the plugin for the Firebase ID token from the already-
    // signed-in native state via getIdToken(). This goes through the
    // Capacitor bridge (no WebView network hop) and returns instantly.
    await FirebaseAuthentication.signInWithGoogle();
    const { token } = await FirebaseAuthentication.getIdToken({ forceRefresh: true });
    if (!token) throw new Error("Native plugin returned an empty Firebase ID token");
    return token;
  }
  const auth = await getFirebaseAuth();
  const provider = new GoogleAuthProvider();
  provider.addScope("email");
  provider.addScope("profile");
  const result = await signInWithPopup(auth, provider);
  return result.user.getIdToken(true);
}

/** Sign in with Apple — required for App Store submission. */
export async function signInWithApple() {
  if (isNativeCapacitor()) {
    // Same rationale as signInWithGoogle above — bypass the JS SDK's
    // signInWithCredential (hangs in WKWebView) and read the Firebase ID
    // token straight from the native SDK via the plugin's getIdToken().
    await FirebaseAuthentication.signInWithApple();
    const { token } = await FirebaseAuthentication.getIdToken({ forceRefresh: true });
    if (!token) throw new Error("Native plugin returned an empty Firebase ID token");
    return token;
  }
  const auth = await getFirebaseAuth();
  const provider = new OAuthProvider("apple.com");
  provider.addScope("email");
  provider.addScope("name");
  const result = await signInWithPopup(auth, provider);
  return result.user.getIdToken(true);
}

export async function signOutFirebase() {
  if (!isFirebaseConfigured()) return;
  try {
    if (isNativeCapacitor()) {
      // v88.8 — On native, only sign out via the plugin. Skip the JS-SDK
      // `fbSignOut(auth)` call because Firebase JS SDK's sign-out issues
      // a network request to `securetoken.googleapis.com` that can hang
      // inside Capacitor WKWebView, blocking any downstream logic (error
      // toasts, redirects, etc). The native plugin's signOut already
      // handles the actual sign-out on the native Firebase SDK — which is
      // the ONLY SDK that was signed in on native (per skipNativeAuth:false).
      await FirebaseAuthentication.signOut();
      return;
    }
    const auth = await getFirebaseAuth();
    await fbSignOut(auth);
  } catch (_) { /* non-fatal */ }
}

// ---------------------------------------------------------------------------
// Email / Password — works identically on Web, Android and iOS (no native
// plugin needed; the web SDK talks directly to Firebase Auth REST).
// ---------------------------------------------------------------------------

/** Sign in an existing user with email + password. Returns the ID token. */
export async function signInWithEmail(email, password) {
  const auth = await getFirebaseAuth();
  const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
  return cred.user.getIdToken(true);
}

/** Create a new user with email + password. Returns the ID token.
 *  Optionally sets a display name so the backend picks it up on first login.
 *  Also fires a verification email in the background — non-blocking. */
export async function signUpWithEmail(email, password, displayName) {
  const auth = await getFirebaseAuth();
  const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
  if (displayName && displayName.trim()) {
    try { await updateProfile(cred.user, { displayName: displayName.trim() }); }
    catch (_e) { /* non-fatal */ }
  }
  // Fire-and-forget verification email — user can log in without it, but
  // we ping them so they can verify at their leisure.
  try { await sendEmailVerification(cred.user); } catch (_e) { /* non-fatal */ }
  return cred.user.getIdToken(true);
}

/** Resend the verification email to the currently-signed-in Firebase user. */
export async function resendVerificationEmail() {
  const auth = await getFirebaseAuth();
  if (!auth.currentUser) throw new Error("Not signed in to Firebase");
  await sendEmailVerification(auth.currentUser);
}

/** Force-refresh the Firebase user, return whether their email is verified.
 *  Called from the Profile page after a user clicks the verification link
 *  in their inbox and returns to the app. */
export async function checkEmailVerified() {
  const auth = await getFirebaseAuth();
  if (!auth.currentUser) throw new Error("Not signed in to Firebase");
  await auth.currentUser.reload();
  // Also refresh the ID token so backend receives the new email_verified claim
  try { await auth.currentUser.getIdToken(true); } catch (_e) { /* non-fatal */ }
  return !!auth.currentUser.emailVerified;
}

/** Send a password-reset email to the given address. */
export async function sendResetEmail(email) {
  const auth = await getFirebaseAuth();
  await sendPasswordResetEmail(auth, email.trim());
}
